/**
 * AutomationRuleEditor.tsx
 *
 * Visual Rule Builder and Dry-Run Sandbox for TaskFlow Automation Engine.
 * Allows creating event triggers (onStatusChange, onPriorityChange, onAllChildrenDone),
 * configuring actions (status, priority, tags, completedDate), and testing in a safe sandbox.
 */

import React, { useState } from 'react';
import type { TaskFlowItem } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import {
  globalAutomationEngine,
  type AutomationRule,
  type AutomationTriggerType,
  type AutomationActionType,
} from '../../../plugins/taskflow/engine/taskflow-automation.js';

export interface AutomationRuleEditorProps {
  items: TaskFlowItem[];
  onClose: () => void;
}

export const AutomationRuleEditor: React.FC<AutomationRuleEditorProps> = ({
  items,
  onClose,
}) => {
  const [rules, setRules] = useState<AutomationRule[]>(() => globalAutomationEngine.getRules());
  const [editingRule, setEditingRule] = useState<Partial<AutomationRule>>({
    name: 'Auto-Complete Parent on All Done',
    enabled: true,
    trigger: { type: 'onAllChildrenDone' },
    actions: [{ type: 'setStatus', status: 'done' }],
  });

  const [sandboxItemId, setSandboxItemId] = useState<string>(items[0]?.id || '');
  const [sandboxResult, setSandboxResult] = useState<any>(null);

  const handleSaveRule = () => {
    if (!editingRule.name || !editingRule.trigger) return;

    const newRule: AutomationRule = {
      id: editingRule.id || `rule-${Date.now()}`,
      name: editingRule.name,
      enabled: editingRule.enabled ?? true,
      trigger: editingRule.trigger as any,
      actions: editingRule.actions || [{ type: 'setStatus', status: 'done' }],
    };

    globalAutomationEngine.registerRule(newRule);
    setRules(globalAutomationEngine.getRules());
    setEditingRule({
      name: '',
      enabled: true,
      trigger: { type: 'onStatusChange', toStatus: 'done' },
      actions: [{ type: 'setCompletedDate', value: '{{now}}' }],
    });
  };

  const handleToggleRule = (rule: AutomationRule) => {
    const updated = { ...rule, enabled: !rule.enabled };
    globalAutomationEngine.registerRule(updated);
    setRules(globalAutomationEngine.getRules());
  };

  const handleDeleteRule = (ruleId: string) => {
    globalAutomationEngine.removeRule(ruleId);
    setRules(globalAutomationEngine.getRules());
  };

  const handleRunSandbox = () => {
    const item = items.find((i) => i.id === sandboxItemId);
    if (!item || !editingRule.trigger) return;

    const res = globalAutomationEngine.dryRunRule(
      editingRule as AutomationRule,
      item,
      { status: editingRule.trigger.toStatus || 'done' }
    );
    setSandboxResult(res);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '520px',
        maxWidth: '92vw',
        backgroundColor: 'var(--theme-bg-modal, rgba(15, 23, 42, 0.96))',
        borderLeft: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
        boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(20px)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.08))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>⚡</span>
          <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0, color: 'var(--theme-text-primary, #f8fafc)' }}>
            Workflow Automations & Rules
          </h2>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted, #94a3b8)', cursor: 'pointer', fontSize: '18px' }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Active Rules List */}
        <div>
          <h3 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--theme-text-secondary, #94a3b8)', margin: '0 0 8px 0' }}>
            Active Rules ({rules.length})
          </h3>

          {rules.length === 0 ? (
            <div style={{ fontSize: '12px', color: 'var(--theme-text-muted, #64748b)', padding: '8px 0' }}>
              No automation rules created yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {rules.map((r) => (
                <div
                  key={r.id}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.03))',
                    border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginTop: '2px' }}>
                      Trigger: <code>{r.trigger.type}</code> ➔ Actions: {r.actions.map((a) => a.type).join(', ')}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleRule(r)}
                      style={{
                        padding: '3px 8px',
                        fontSize: '10px',
                        fontWeight: 700,
                        backgroundColor: r.enabled ? 'var(--theme-accent, #6366f1)' : 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.1))',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      {r.enabled ? 'ON' : 'OFF'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(r.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted, #94a3b8)', cursor: 'pointer', fontSize: '12px' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rule Builder Form */}
        <div
          style={{
            backgroundColor: 'var(--theme-bg-card, rgba(15, 23, 42, 0.7))',
            border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <h3 style={{ fontSize: '13px', fontWeight: 700, margin: 0, color: 'var(--theme-text-primary, #fff)' }}>
            Create New Rule
          </h3>

          <div>
            <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #94a3b8)', textTransform: 'uppercase' }}>
              Rule Name
            </label>
            <input
              type="text"
              placeholder="e.g. Set Completed Date when Done"
              value={editingRule.name || ''}
              onChange={(e) => setEditingRule((prev) => ({ ...prev, name: e.target.value }))}
              style={{
                width: '100%',
                marginTop: '4px',
                padding: '6px 10px',
                backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.3))',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '12px',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #94a3b8)', textTransform: 'uppercase' }}>
              Trigger Event
            </label>
            <select
              value={editingRule.trigger?.type || 'onStatusChange'}
              onChange={(e) =>
                setEditingRule((prev) => ({
                  ...prev,
                  trigger: { type: e.target.value as AutomationTriggerType, toStatus: 'done' },
                }))
              }
              style={{
                width: '100%',
                marginTop: '4px',
                padding: '6px 10px',
                backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.3))',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '12px',
                outline: 'none',
              }}
            >
              <option value="onStatusChange">When Status Changes</option>
              <option value="onAllChildrenDone">When All Children Are Done</option>
              <option value="onPriorityChange">When Priority Changes</option>
              <option value="onDueDateApproaching">When Due Date Is Approaching</option>
            </select>
          </div>

          <button
            onClick={handleSaveRule}
            style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: 'var(--theme-accent, #6366f1)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Save Rule
          </button>
        </div>

        {/* Dry-Run Sandbox */}
        <div
          style={{
            backgroundColor: 'var(--theme-bg-subtle, rgba(0, 0, 0, 0.3))',
            border: '1px dashed var(--theme-border, rgba(255, 255, 255, 0.15))',
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ fontSize: '12px', fontWeight: 700, margin: 0, color: 'var(--theme-text-primary, #fff)' }}>
              🧪 Dry-Run Rule Sandbox
            </h4>
            <button
              onClick={handleRunSandbox}
              style={{
                padding: '4px 10px',
                backgroundColor: 'var(--theme-accent, #6366f1)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Test Simulation
            </button>
          </div>

          <select
            value={sandboxItemId}
            onChange={(e) => setSandboxItemId(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.3))',
              border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '12px',
              outline: 'none',
            }}
          >
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.title} ({i.status})
              </option>
            ))}
          </select>

          {sandboxResult && (
            <div
              style={{
                marginTop: '6px',
                padding: '8px',
                backgroundColor: sandboxResult.triggered
                  ? 'var(--theme-status-success-bg, rgba(52, 211, 153, 0.1))'
                  : 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.05))',
                border: sandboxResult.triggered
                  ? '1px solid var(--theme-status-success-text, #34d399)'
                  : '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.1))',
                borderRadius: '6px',
                fontSize: '11px',
              }}
            >
              <div style={{ fontWeight: 700, color: sandboxResult.triggered ? 'var(--theme-status-success-text, #34d399)' : '#94a3b8' }}>
                {sandboxResult.triggered ? '✅ Rule Triggered Successfully' : '⚠️ Rule Did Not Trigger'}
              </div>
              {sandboxResult.triggered && (
                <div style={{ marginTop: '4px', color: 'var(--theme-text-secondary, #cbd5e1)' }}>
                  Resulting Tags: {JSON.stringify(sandboxResult.simulatedItem.tags)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
